/**
 * Dados simulados do modulo Admin - Empresas (RNF19) -- NAO E PRODUCAO.
 *
 * Como o numero nasce:
 *  1. Existe UMA fonte: `vendasDoDia(empresa, dia)`. Semana, mes, total do
 *     periodo e o periodo anterior sao SOMAS dessa serie diaria. E o que faz
 *     "recuperado no periodo" bater com a soma das barras semanais, com o
 *     extrato mensal e com a comparacao -- geradores independentes
 *     divergiriam na primeira conferencia.
 *  2. `h = hash(companyId)` e `feeBps` diferenciam as empresas: ritmo de
 *     vendas, taxa de recuperacao e comissao. Uma onda de quatro semanas
 *     (tambem derivada de `h`) faz o periodo atual e o anterior diferirem de
 *     verdade, para a comparacao ter o que mostrar. Nenhum Math.random.
 *  3. A janela e a do cabecalho, recortada pela DATA DE CRIACAO da empresa:
 *     nao existe venda antes da conta existir. O periodo ANTERIOR (mesmo
 *     tamanho, logo antes) so existe se a conta ja existia no inicio dele;
 *     senao e `null` ("sem base"), nunca um zero que viraria "+100%".
 *  4. O CENARIO de cada empresa espelha o da Central (features/admin-central/
 *     mock.ts), pela posicao no catalogo: 0 saudavel, 1 WhatsApp
 *     desconectado, 2 integracao muda ha 3 dias, e quem esta em implantacao
 *     ainda sem webhook. Modulos nao importam um do outro, entao a regra e
 *     repetida aqui de proposito: a mesma empresa nao pode estar "critica" na
 *     Central e "saudavel" no cadastro.
 *  5. O GENERICO (checkout proprio) aparece conectado nas empresas ativas com
 *     `h % 4 === 0`: e o caso real de quem vende por Hotmart e tambem por um
 *     checkout proprio.
 *  6. A linha do tempo da conta (`history`) NAO e recortada pela janela: e a
 *     biografia da empresa. Excecao deliberada, anotada no rodape da aba.
 */

import { AGORA_SIMULADO, menosMinutos } from '../../lib/api';
import { EMPRESAS, empresaPorId, equipeDaEmpresa, ofertaCiclica, ofertasDaEmpresa } from '../../lib/mock/catalogo';
import { formatPercentFromBps } from '../../lib/format';
import type { CompanySummary, PeriodRange } from '../../types';
import { hashSimulado, urlDoWebhook, uuidDeterministico } from './identidade';
import type {
  AccountEvent,
  CommissionMonth,
  CompaniesOverview,
  CompaniesTotals,
  CompanyDetail,
  CompanyIntegrations,
  CompanyOverviewItem,
  CompanyPeriodMetrics,
  CompanySetup,
  InvoiceStatus,
  PeriodBounds,
  ProvedorIntegracao,
  WeeklyPoint,
} from './types';
import { MODELO_COBRANCA, PROVEDOR_INFO } from './vocabulario';

/**
 * Provedores que a PREVIA simula: os quatro de antes da onda 1. O vocabulario
 * (`PROVEDORES`) ja tem os oito do motor, mas o gerador simulado continua com
 * esta lista para a previa nao mudar de comportamento (mesmas integracoes,
 * mesmo "X de N conectados"); so o servidor real lista a onda 1.
 */
const PROVEDORES_DA_PREVIA: readonly ProvedorIntegracao[] = ['kiwify', 'hotmart', 'shopify', 'generic'];

const DIA_MS = 86_400_000;
const MINUTOS_POR_DIA = 1_440;
const MAX_MESES = 12;
const DIAS_SEM_EVENTO = 3;
const MESES_CURTOS: readonly string[] = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// -----------------------------------------------------------------------------
// Datas (tudo em horario LOCAL, como a janela do cabecalho)
// -----------------------------------------------------------------------------

function meiaNoiteLocal(data: Date): Date {
  return new Date(data.getFullYear(), data.getMonth(), data.getDate());
}

function somarDias(data: Date, dias: number): Date {
  return new Date(data.getFullYear(), data.getMonth(), data.getDate() + dias);
}

function dataIso(data: Date): string {
  const y = data.getFullYear();
  const m = String(data.getMonth() + 1).padStart(2, '0');
  const d = String(data.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function rotuloDiaMes(data: Date): string {
  return `${String(data.getDate()).padStart(2, '0')}/${String(data.getMonth() + 1).padStart(2, '0')}`;
}

function chaveMes(data: Date): string {
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
}

/** "2026-09" -> "set/26". */
export function rotuloDoMes(chave: string): string {
  const [ano, mes] = chave.split('-');
  const idx = Number(mes ?? '1') - 1;
  return `${MESES_CURTOS[idx] ?? mes ?? ''}/${(ano ?? '').slice(2)}`;
}

interface Janela {
  readonly from: Date;
  readonly to: Date;
  readonly dias: readonly Date[];
}

/** Janela pedida recortada pela criacao da empresa; `dias` sao as meias-noites locais cobertas. */
function recortar(company: CompanySummary, fromPedido: Date, to: Date): Janela {
  const criada = new Date(company.createdAt);
  const from = fromPedido.getTime() < criada.getTime() ? criada : fromPedido;
  const dias: Date[] = [];
  for (let d = meiaNoiteLocal(from); d.getTime() < to.getTime() && dias.length < 400; d = somarDias(d, 1)) {
    dias.push(d);
  }
  return { from, to, dias };
}

function janelaDaEmpresa(company: CompanySummary, range: PeriodRange): Janela {
  const to = range.to === null ? somarDias(meiaNoiteLocal(AGORA_SIMULADO), 1) : new Date(range.to);
  const fromPedido = range.from === null ? new Date(company.createdAt) : new Date(range.from);
  return recortar(company, fromPedido, to);
}

/** Mesma duracao, logo antes. `null` em "desde o inicio": nao ha anterior honesto. */
function periodoAnterior(range: PeriodRange): PeriodBounds | null {
  if (range.from === null || range.to === null) {
    return null;
  }
  const inicio = new Date(range.from).getTime();
  const fim = new Date(range.to).getTime();
  if (Number.isNaN(inicio) || Number.isNaN(fim) || fim <= inicio) {
    return null;
  }
  return { start: new Date(inicio - (fim - inicio)).toISOString(), end: new Date(inicio).toISOString() };
}

// -----------------------------------------------------------------------------
// Cenario (espelha a Central) e a serie diaria canonica
// -----------------------------------------------------------------------------

type Cenario = 'saudavel' | 'whatsapp_desconectado' | 'integracao_muda' | 'sem_webhook';

const CICLO_DE_CENARIOS: readonly Cenario[] = ['saudavel', 'whatsapp_desconectado', 'integracao_muda'];

function cenarioDa(company: CompanySummary): Cenario {
  if (company.status === 'onboarding') {
    return 'sem_webhook';
  }
  const posicao = EMPRESAS.findIndex((e) => e.id === company.id);
  return CICLO_DE_CENARIOS[Math.max(0, posicao) % CICLO_DE_CENARIOS.length] ?? 'saudavel';
}

interface VendasDoDia {
  readonly count: number;
  readonly amountCents: number;
  /** Vendas que a regua teria feito se o canal estivesse no ar (o abandono chegou igual). */
  readonly perdidas: number;
}

const SEM_VENDA: VendasDoDia = { count: 0, amountCents: 0, perdidas: 0 };

function vendasDoDia(company: CompanySummary, h: number, dia: Date): VendasDoDia {
  if (company.status !== 'ativa') {
    return SEM_VENDA;
  }
  const criada = new Date(company.createdAt);
  const n = Math.floor((dia.getTime() - criada.getTime()) / DIA_MS);
  if (n < 0) {
    return SEM_VENDA;
  }
  const cenario = cenarioDa(company);
  const diasAteAgora = Math.floor((AGORA_SIMULADO.getTime() - dia.getTime()) / DIA_MS);
  // Integracao muda: sem abandono chegando nos ultimos dias, sem venda recuperada.
  if (cenario === 'integracao_muda' && diasAteAgora >= 0 && diasAteAgora < DIAS_SEM_EVENTO) {
    return SEM_VENDA;
  }
  const porDia = 1 + (h % 4);
  const fimDeSemana = dia.getDay() === 0 || dia.getDay() === 6;
  // Onda de quatro semanas: blocos de 28 dias alternam o ritmo.
  const onda = Math.floor(((n + h) % 56) / 28);
  // WhatsApp fora nos ultimos dias: o toque de WhatsApp nao sai e a recuperacao cai.
  const semWhatsapp = cenario === 'whatsapp_desconectado' && diasAteAgora >= 0 && diasAteAgora < 5 ? 1 : 0;
  const potencial = Math.max(0, porDia + ((n * 7 + h) % 3) - 1 - (fimDeSemana ? 1 : 0) + onda);
  const count = Math.max(0, potencial - semWhatsapp);
  let amountCents = 0;
  for (let k = 0; k < count; k += 1) {
    amountCents += ofertaCiclica(company.id, n * 3 + k).priceCents;
  }
  return { count, amountCents, perdidas: potencial - count };
}

function comissaoDe(amountCents: number, feeBps: number): number {
  return Math.round((amountCents * feeBps) / 10_000);
}

function montarMetricas(company: CompanySummary, h: number, janela: Janela): CompanyPeriodMetrics {
  let recoveredCount = 0;
  let recoveredAmountCents = 0;
  let perdidas = 0;
  for (const dia of janela.dias) {
    const v = vendasDoDia(company, h, dia);
    recoveredCount += v.count;
    recoveredAmountCents += v.amountCents;
    perdidas += v.perdidas;
  }
  const commissionCents = comissaoDe(recoveredAmountCents, company.feeBps);
  const taxaBps = 1_700 + (h % 7) * 100;
  // O abandono chega igual quando o canal cai (so a venda se perde): a base
  // inclui as perdidas, e e isso que faz a TAXA cair no cenario de WhatsApp fora.
  // Em implantacao ha abandono chegando (a regua ja roda) e ainda nenhuma venda.
  const vendasDaBase = recoveredCount + perdidas;
  const abandonedCount =
    vendasDaBase > 0 ? Math.round((vendasDaBase * 10_000) / taxaBps) : company.status === 'onboarding' ? janela.dias.length * (1 + (h % 3)) : 0;
  return {
    abandonedCount,
    recoveredCount,
    recoveredAmountCents,
    commissionCents,
    netAmountCents: recoveredAmountCents - commissionCents,
    averageTicketCents: recoveredCount === 0 ? 0 : Math.floor(recoveredAmountCents / recoveredCount),
    recoveryRateBps: abandonedCount === 0 ? 0 : Math.round((recoveredCount * 10_000) / abandonedCount),
  };
}

/** Metricas da janela anterior; `null` se a conta nao existia no inicio dela. */
function montarAnterior(company: CompanySummary, h: number, anterior: PeriodBounds | null): CompanyPeriodMetrics | null {
  if (anterior === null || company.createdAt > anterior.start) {
    return null;
  }
  return montarMetricas(company, h, recortar(company, new Date(anterior.start), new Date(anterior.end)));
}

function montarSemanas(company: CompanySummary, h: number, janela: Janela): readonly WeeklyPoint[] {
  const pontos: WeeklyPoint[] = [];
  for (let i = 0; i < janela.dias.length; i += 7) {
    const inicio = janela.dias[i];
    if (inicio === undefined) {
      break;
    }
    let amount = 0;
    for (const dia of janela.dias.slice(i, i + 7)) {
      amount += vendasDoDia(company, h, dia).amountCents;
    }
    pontos.push({
      weekStart: dataIso(inicio),
      label: rotuloDiaMes(inicio),
      recoveredAmountCents: amount,
      commissionCents: comissaoDe(amount, company.feeBps),
    });
  }
  return pontos;
}

function statusDoMes(chave: string): InvoiceStatus {
  const atual = chaveMes(AGORA_SIMULADO);
  if (chave === atual) {
    return 'a_faturar';
  }
  const anterior = chaveMes(new Date(AGORA_SIMULADO.getFullYear(), AGORA_SIMULADO.getMonth() - 1, 1));
  return chave === anterior ? 'faturado' : 'pago';
}

/** Um item por mes que toca a janela, do mais recente para o mais antigo (teto de 12). */
function montarMeses(company: CompanySummary, h: number, janela: Janela): readonly CommissionMonth[] {
  const porMes = new Map<string, number>();
  for (const dia of janela.dias) {
    const chave = chaveMes(dia);
    porMes.set(chave, (porMes.get(chave) ?? 0) + vendasDoDia(company, h, dia).amountCents);
  }
  return [...porMes.entries()]
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .slice(0, MAX_MESES)
    .map(([month, recoveredAmountCents]) => ({
      month,
      recoveredAmountCents,
      commissionCents: comissaoDe(recoveredAmountCents, company.feeBps),
      feeBps: company.feeBps,
      status: statusDoMes(month),
    }));
}

// -----------------------------------------------------------------------------
// Integracoes, prontidao e biografia da conta
// -----------------------------------------------------------------------------

function celularFicticio(h: number, deslocamento: number): string {
  // Padrao do guia: +55 11 9999x-xxxx (nove digitos depois do DDD).
  return `+55119999${String((h * 7 + deslocamento) % 100_000).padStart(5, '0')}`;
}

function usaGenerico(company: CompanySummary, h: number): boolean {
  return company.status === 'ativa' && h % 4 === 0;
}

function provedoresConectados(company: CompanySummary, h: number): readonly ProvedorIntegracao[] {
  return PROVEDORES_DA_PREVIA.filter((p) => (p === 'generic' ? usaGenerico(company, h) : company.providers.includes(p)));
}

function ultimoWebhook(company: CompanySummary, h: number): string | null {
  switch (cenarioDa(company)) {
    case 'saudavel':
      return menosMinutos(4 + (h % 40));
    case 'whatsapp_desconectado':
      return menosMinutos(12 + (h % 30));
    case 'integracao_muda':
      return menosMinutos(DIAS_SEM_EVENTO * MINUTOS_POR_DIA + 37);
    case 'sem_webhook':
      return null;
  }
}

function montarIntegracoes(company: CompanySummary, h: number): CompanyIntegrations {
  const implantando = company.status === 'onboarding';
  const conectados = provedoresConectados(company, h);
  const ultimo = ultimoWebhook(company, h);
  let primeiroConectado = true;
  let ordem = 0;
  const webhooks = PROVEDORES_DA_PREVIA.map((provider) => {
    const connected = conectados.includes(provider);
    // Em implantacao, o primeiro provedor conectado ainda esta sem segredo: e o
    // "colei a URL mas nao mandei o segredo" que gera 401 silencioso no motor.
    const semSegredo = implantando && connected && primeiroConectado;
    if (connected) {
      primeiroConectado = false;
    }
    const secretState = connected && !semSegredo ? ('configured' as const) : ('missing' as const);
    // O mais recente e o do primeiro provedor em dia; os demais vieram um pouco antes.
    const emDia = connected && secretState === 'configured' && ultimo !== null;
    const lastEventAt = emDia ? new Date(new Date(ultimo).getTime() - ordem * (47 + (h % 90)) * 60_000).toISOString() : null;
    if (emDia) {
      ordem += 1;
    }
    return { provider, connected, webhookUrl: urlDoWebhook(provider, company.id), secretState, lastEventAt };
  });
  const whatsappFora = implantando || cenarioDa(company) === 'whatsapp_desconectado';
  return {
    webhooks,
    whatsapp: {
      instanceName: `cf-${company.slug}`,
      phoneE164: celularFicticio(h, 11),
      state: whatsappFora ? 'disconnected' : 'connected',
    },
    voice: {
      assistantName: `Assistente ${company.name}`,
      assistantId: `asst_${uuidDeterministico(`${company.id}:vapi`).slice(0, 8)}`,
      originPhoneE164: celularFicticio(h, 4_321),
      state: implantando ? 'pending' : 'ready',
    },
  };
}

function montarSetup(company: CompanySummary, h: number, integracoes: CompanyIntegrations): CompanySetup {
  const conectados = integracoes.webhooks.filter((w) => w.connected);
  return {
    connectedProviders: conectados.map((w) => w.provider),
    providersMissingSecret: conectados.filter((w) => w.secretState === 'missing').map((w) => w.provider),
    whatsapp: integracoes.whatsapp.state,
    voice: integracoes.voice.state,
    lastWebhookAt: ultimoWebhook(company, h),
    recurringOffers: ofertasDaEmpresa(company.id).filter((o) => o.active && o.recurring).length,
    salesReps: equipeDaEmpresa(company.id).filter((m) => m.role === 'comercial').length,
  };
}

function montarHistorico(company: CompanySummary, h: number): readonly AccountEvent[] {
  const criada = new Date(company.createdAt);
  const em = (dias: number, horas: number): string => new Date(criada.getTime() + dias * DIA_MS + horas * 3_600_000).toISOString();
  const dono = equipeDaEmpresa(company.id).find((m) => m.role === 'owner')?.name ?? company.contactName;
  const vendedoras = equipeDaEmpresa(company.id).filter((m) => m.role === 'comercial');
  const cenario = cenarioDa(company);
  const eventos: AccountEvent[] = [
    {
      id: `SIMULADO-evento-${company.slug}-criada`,
      kind: 'criada',
      at: company.createdAt,
      title: 'Conta criada',
      description: `Cadastrada pela equipe Converte Fácil com comissão de ${formatPercentFromBps(company.feeBps)} · segmento: ${company.segment}.`,
    },
    {
      id: `SIMULADO-evento-${company.slug}-dono`,
      kind: 'equipe',
      at: em(0, 1),
      title: 'Dono convidado',
      description: `${dono} recebeu o convite de acesso ao painel.`,
    },
    ...provedoresConectados(company, h).map((provider, i) => ({
      id: `SIMULADO-evento-${company.slug}-${provider}`,
      kind: 'integracao' as const,
      at: em(1 + i, 2 + i),
      title: `${PROVEDOR_INFO[provider].curto} conectado`,
      description:
        provider === 'generic'
          ? 'Checkout próprio enviando o contrato canônico, assinado com HMAC-SHA256.'
          : 'URL de webhook colada no provedor e segredo de assinatura configurado.',
    })),
    ...vendedoras.map((m, i) => ({
      id: `SIMULADO-evento-${company.slug}-vendedora-${i}`,
      kind: 'equipe' as const,
      at: em(6 + i, 10),
      title: 'Vendedora adicionada',
      description: `${m.name} entrou com papel Comercial: atende quem responde à régua e registra venda assistida.`,
    })),
  ];
  if (company.billingModel !== 'a_definir') {
    eventos.push({
      id: `SIMULADO-evento-${company.slug}-cobranca`,
      kind: 'ajuste',
      at: em(2, 5),
      title: 'Modelo de cobrança registrado',
      description: `${MODELO_COBRANCA[company.billingModel].label}. Decisão final do dono ainda pendente.`,
    });
  }
  if (company.status === 'ativa') {
    const primeiraVenda = ofertaCiclica(company.id, 0);
    eventos.push(
      {
        id: `SIMULADO-evento-${company.slug}-webhook`,
        kind: 'primeiro_webhook',
        at: em(2 + (h % 2), 9),
        title: 'Primeiro webhook recebido',
        description: 'Um checkout abandonado há 30 minutos entrou na régua. O motor está vivo.',
      },
      {
        id: `SIMULADO-evento-${company.slug}-recuperacao`,
        kind: 'primeira_recuperacao',
        at: em(4 + (h % 3), 15),
        title: 'Primeira recuperação',
        description: `${primeiraVenda.name} pago após a ligação da IA. Comissão creditada ao último contato do motor (ADR-001).`,
      },
      {
        id: `SIMULADO-evento-${company.slug}-regua`,
        kind: 'ajuste',
        at: em(20, 11),
        title: 'Régua ajustada',
        description: 'Horário permitido de contato reduzido para dias úteis, das 9h às 20h.',
      },
    );
    if (company.funnels.includes('expirados')) {
      eventos.push({
        id: `SIMULADO-evento-${company.slug}-expirados`,
        kind: 'ajuste',
        at: em(35, 14),
        title: 'Funil de expirados ativado',
        description: 'Assinaturas que já venceram passam a entrar na régua D+15 / D+30 / D+60 / D+90.',
      });
    }
  }
  if (cenario === 'whatsapp_desconectado') {
    eventos.push({
      id: `SIMULADO-evento-${company.slug}-whatsapp-caiu`,
      kind: 'integracao',
      at: menosMinutos(5 * MINUTOS_POR_DIA - 90),
      title: 'Sessão do WhatsApp caiu',
      description: `A instância cf-${company.slug} perdeu a sessão do aparelho. É preciso ler o QR code de novo na Evolution.`,
    });
  }
  if (cenario === 'integracao_muda') {
    eventos.push({
      id: `SIMULADO-evento-${company.slug}-ultimo-webhook`,
      kind: 'integracao',
      at: menosMinutos(DIAS_SEM_EVENTO * MINUTOS_POR_DIA + 37),
      title: 'Último webhook recebido',
      description: 'Desde então nenhum evento chegou. Confira se a URL e o segredo continuam no painel do provedor.',
    });
  }
  return eventos
    .filter((e) => new Date(e.at).getTime() <= AGORA_SIMULADO.getTime())
    .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
}

// -----------------------------------------------------------------------------
// Geradores expostos
// -----------------------------------------------------------------------------

const TOTAIS_ZERADOS: CompaniesTotals = { recoveredAmountCents: 0, commissionCents: 0, netAmountCents: 0, recoveredCount: 0, abandonedCount: 0 };

function somar(acc: CompaniesTotals, m: CompanyPeriodMetrics): CompaniesTotals {
  return {
    recoveredAmountCents: acc.recoveredAmountCents + m.recoveredAmountCents,
    commissionCents: acc.commissionCents + m.commissionCents,
    netAmountCents: acc.netAmountCents + m.netAmountCents,
    recoveredCount: acc.recoveredCount + m.recoveredCount,
    abandonedCount: acc.abandonedCount + m.abandonedCount,
  };
}

export function simularVisaoEmpresas(range: PeriodRange): CompaniesOverview {
  const anterior = periodoAnterior(range);
  const items: CompanyOverviewItem[] = EMPRESAS.map((company) => {
    const h = hashSimulado(company.id);
    const janela = janelaDaEmpresa(company, range);
    const integracoes = montarIntegracoes(company, h);
    return {
      companyId: company.id,
      activeOffers: ofertasDaEmpresa(company.id).filter((o) => o.active).length,
      metrics: montarMetricas(company, h, janela),
      previous: montarAnterior(company, h, anterior),
      weeklyRecoveredCents: montarSemanas(company, h, janela)
        .slice(-12)
        .map((p) => p.recoveredAmountCents),
      setup: montarSetup(company, h, integracoes),
    };
  });
  const totals = items.reduce((acc, item) => somar(acc, item.metrics), TOTAIS_ZERADOS);
  // Empresa sem base anterior (ainda nao existia) contribui zero: e o que a plataforma faturou de fato.
  const previousTotals = anterior === null ? null : items.reduce((acc, item) => (item.previous === null ? acc : somar(acc, item.previous)), TOTAIS_ZERADOS);
  const maisAntiga = EMPRESAS.reduce((min, c) => (c.createdAt < min ? c.createdAt : min), AGORA_SIMULADO.toISOString());
  return {
    items,
    totals,
    previousTotals,
    periodStart: range.from ?? maisAntiga,
    periodEnd: range.to ?? AGORA_SIMULADO.toISOString(),
    previousPeriod: anterior,
  };
}

/** `null` quando o id nao esta no catalogo: a pagina mostra o vazio, nao inventa empresa. */
export function simularDetalheEmpresa(companyId: string, range: PeriodRange): CompanyDetail | null {
  const company = empresaPorId(companyId);
  if (company === null) {
    return null;
  }
  const h = hashSimulado(company.id);
  const janela = janelaDaEmpresa(company, range);
  const anterior = periodoAnterior(range);
  const integrations = montarIntegracoes(company, h);
  const previous = montarAnterior(company, h, anterior);
  return {
    company,
    metrics: montarMetricas(company, h, janela),
    previous,
    previousPeriod: previous === null ? null : anterior,
    weekly: montarSemanas(company, h, janela),
    offers: ofertasDaEmpresa(company.id),
    team: equipeDaEmpresa(company.id),
    integrations,
    setup: montarSetup(company, h, integrations),
    commissionHistory: montarMeses(company, h, janela),
    history: montarHistorico(company, h),
    periodStart: janela.from.toISOString(),
    periodEnd: janela.to.toISOString(),
  };
}
